import { useStickToBottom } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useStickToBottom'
import { getAgenticConversationTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticConversation'
import type { AgenticConversationTurn } from 'features/knowledgeDocumentViewer/types'
import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'

import { AgenticChatTurnRows } from './AgenticChatTurnRows'
import { timelineItemEnter } from './agenticSearchChatStyles'
import { AgenticTurnRows, TimelineConnector } from './AgenticSearchTimelineRows'

type AgenticSearchTimelineVariant = 'header' | 'sidePanel'

const Timeline = styled.div<{ $variant: AgenticSearchTimelineVariant }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  ${(props) =>
    props.$variant === 'header'
      ? `
        max-height: 9.25rem;
        padding: 0.25rem 0.5rem 0.5rem;
        scrollbar-gutter: stable;
      `
      : `
        flex: 1;
        min-height: 0;
        padding: 0.25rem 0 0.5rem 0.25rem;
        scrollbar-gutter: stable;
        scrollbar-width: thin;
      `}
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: ${(props) =>
      props.$variant === 'sidePanel' ? '0.25rem' : '0.5rem'};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 9999rem;
    background: var(--border);
  }
`

const TimelineContent = styled.div<{ $variant: AgenticSearchTimelineVariant }>`
  position: relative;
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.$variant === 'sidePanel'
      ? `
        gap: 1rem;
        padding-right: 0.75rem;
      `
      : `
        &::before {
          content: '';
          position: absolute;
          top: 0.75rem;
          bottom: 0.75rem;
          left: 0.75rem;
          width: 0.0625rem;
          transform: translateX(-50%);
          border-radius: 9999rem;
          background: var(--border);
        }
      `}
`

const TurnBlock = styled.div<{ $animate: boolean }>`
  display: flex;
  flex-direction: column;
  scroll-margin-block: 0.25rem;
  ${(props) =>
    props.$animate
      ? `
        animation: ${timelineItemEnter} 180ms ease-out both;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
        }
      `
      : ''}
`

interface AgenticSearchTimelineProps {
  turns: AgenticConversationTurn[]
  variant?: AgenticSearchTimelineVariant
}

export function AgenticSearchTimeline({
  turns,
  variant = 'header',
}: AgenticSearchTimelineProps): React.ReactElement {
  const timelineContentRef = useRef<HTMLDivElement>(null)
  const isSidePanel = variant === 'sidePanel'

  const { scrollRef, onScroll } = useStickToBottom({
    contentRef: timelineContentRef,
    scrollDeps: [turns],
    resetWhenEmpty: turns.length === 0,
  })

  const latestActionableTurnId = useMemo(() => {
    if (!isSidePanel) return undefined
    for (let index = turns.length - 1; index >= 0; index -= 1) {
      const turn = turns[index]
      const status = getAgenticConversationTurnStatus(turn)
      if (status === 'complete' || status === 'error') {
        return turn.id
      }
    }
    return undefined
  }, [isSidePanel, turns])

  return (
    <Timeline
      aria-label='Agentic search conversation'
      $variant={variant}
      ref={scrollRef}
      role='log'
      onScroll={onScroll}>
      <TimelineContent $variant={variant} ref={timelineContentRef}>
        {turns.map((turn, index) => (
          <React.Fragment key={turn.id}>
            {!isSidePanel && index > 0 && <TimelineConnector />}
            <TurnBlock $animate={!isSidePanel}>
              {isSidePanel ? (
                <AgenticChatTurnRows
                  showActions={turn.id === latestActionableTurnId}
                  turn={turn}
                />
              ) : (
                <AgenticTurnRows turn={turn} />
              )}
            </TurnBlock>
          </React.Fragment>
        ))}
      </TimelineContent>
    </Timeline>
  )
}
